/**
 * ProcessConditionalElements — repeated visibility passes until stable,
 * capped at 500 (REQ-PRICE-5; research/04 §2.9; research/03 §15.21).
 * Controlling element resolution: select → checked radio → checkbox via
 * globalHelper_Escape'd attribute selectors; checkbox compare values
 * "Checked"/"Unchecked"; visibility via INLINE style.display only;
 * show = "block", hide = "none".
 */
import { globalHelper_Escape } from "../common/common-utils";
import { isNumericTriggerType, matchesCondition } from "./conditional-operators";
import $ from "jquery";

/**
 * #151: whether this calculator has any condition controlled by a number input or slider.
 *
 * The debounced typing path historically ran UpdatePrice ONLY (§7.20) — correct while every
 * controller was something you click, because nothing you could type could change what was
 * visible. A numeric trigger breaks that, so the typing path now re-runs visibility, but only for
 * a calculator that actually has one.
 *
 * Computed once per render and cached: the typing path pays a boolean read per keystroke, not a
 * DOM query, and a calculator with no numeric condition — which is every calculator that existed
 * before #151 — behaves exactly as it did.
 */
let numericTriggerPresent: boolean | null = null;

/** Called when the calculator DOM is (re)built, so a rebuild cannot keep a stale answer. */
export function resetNumericConditionalTriggerCache(): void {
  numericTriggerPresent = null;
}

export function hasNumericConditionalTrigger(): boolean {
  if (numericTriggerPresent === null) {
    numericTriggerPresent =
      document.querySelector(
        '.element[data-conditionaltriggertype="NumberInputNew"], ' +
        '.element[data-conditionaltriggertype="Slider"]',
      ) !== null;
  }
  return numericTriggerPresent;
}

export function ProcessConditionalElements(): void {
  // Repeat passes until visibility stabilises. Cap iterations to prevent
  // infinite loops from circular conditional dependencies.
  var MAX_PASSES = 500;
  var changed = true;
  var pass = 0;
  while (changed && pass < MAX_PASSES) {
    pass++;
    changed = false;
    $(".element").each((d: any, elem: any) => {
      let conditionalElement = $(elem).data("conditionalelement");
      let conditionalElementValue = $(elem).data("conditionalelementvalue");
      // #209: absent attribute (every pre-existing calculator) and any
      // unrecognised value both mean equals — the historical behaviour.
      let conditionalOperator = $(elem).data("conditionaloperator");
      if (conditionalElement !== undefined && conditionalElement !== null) {
        //Check if conditional Element is select
        var escapedConditionalElement = globalHelper_Escape(conditionalElement);
        let selectConditionalElement = document.querySelector(
          'select[data-identifier="' + escapedConditionalElement + '"]',
        );
        let radioConditionalElement = document.querySelector(
          'input[type="radio"][data-identifier="' +
          escapedConditionalElement +
          '"]:checked',
        );
        let checkboxConditionalElement = document.querySelector(
          'input[type="checkbox"][data-identifier="' +
          escapedConditionalElement +
          '"]',
        );
        // #151: a number or slider controller is resolved ONLY when the saved condition records
        // that it meant one. Calculators carry stale references to renamed or deleted elements
        // (issue #191), and without this guard such a reference would suddenly resolve against a
        // same-named number input and reveal an element that has been hidden on a live product
        // page for months. Conditions saved before #151 carry no trigger type and so never take
        // this branch — their resolution is byte-for-byte what it was.
        let numericConditionalElement = isNumericTriggerType(
          $(elem).data("conditionaltriggertype"),
        )
          ? document.querySelector(
              'input[type="number"][data-identifier="' +
              escapedConditionalElement +
              '"], input[type="range"][data-identifier="' +
              escapedConditionalElement +
              '"]',
            )
          : null;

        let selectedValue: any = null;
        if (numericConditionalElement !== null) {
          selectedValue = $(numericConditionalElement).val();
        } else if (selectConditionalElement !== null) {
          selectedValue = $(selectConditionalElement).val();
          // console.log('selectedValue', selectedValue)
        } else if (radioConditionalElement !== null) {
          selectedValue = $(radioConditionalElement).val();
        } else if (checkboxConditionalElement !== null) {
          selectedValue =
            $(checkboxConditionalElement).is(":checked") === true
              ? "Checked"
              : "Unchecked";
          // console.log('checkbox', checkboxConditionalElement)
        }

        // Determine hidden state from the wrapper .element div — it is the
        // exact node we set inline style on, making it the authoritative
        // source regardless of how the browser reports child input visibility.
        // We use elem.style.display (inline) rather than jQuery.is(':hidden') /
        // getComputedStyle because some .element wrappers carry the hide-element
        // CSS class (display:none !important) for UX purposes (formula inputs
        // that shouldn't be user-editable). The !important rule always wins over
        // jQuery.show()/hide() inline style, causing jQuery.is(':hidden') to
        // always return true for these elements regardless of their actual
        // conditional state. Reading the inline style directly is the only
        // reliable way to determine whether the element was conditionally shown.
        var conditionalWrapper: any = document.querySelector(
          '.element[data-element="' + escapedConditionalElement + '"]',
        );
        var isHidden = conditionalWrapper !== null
          ? conditionalWrapper.style.display === "none"
          : false;

        //Show hide the conditional element if the value matches
        if (selectedValue !== null) {
          // Only the value test depends on the operator. The isHidden cascade
          // guard is deliberately OUTSIDE it: a dependent whose controller is
          // itself hidden must stay hidden under every operator, otherwise
          // "is not" — or "less than", which is true of an empty field's 0 —
          // would reveal branches of a collapsed chain.
          var valueMatches = matchesCondition(
            conditionalOperator,
            selectedValue,
            conditionalElementValue,
          );
          var shouldShow = valueMatches && isHidden === false;
          // Use inline style to determine current state — same reason as above.
          var isCurrentlyHidden = elem.style.display === "none";
          if (shouldShow && isCurrentlyHidden) {
            elem.style.display = "block";
            changed = true;
          } else if (!shouldShow && !isCurrentlyHidden) {
            elem.style.display = "none";
            changed = true;
          }
        }
      }
    });
  }
}
